import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Node, Edge } from 'reactflow';
import { useI18n } from '../../i18n';
import { tableauDisplayName } from './TableauElectriqueModal';
import { displayDefaultName } from '../../i18n/defaultNames';

type TFn = ReturnType<typeof useI18n>['t'];
type TKey = Parameters<TFn>[0];

/**
 * SynopticView — vue "Consulter" de la page Architecture.
 *
 * Synoptique de flux d'énergie façon IHM d'onduleur : le canvas SVG est généré à partir
 * des nœuds/liaisons de l'éditeur (ElectricalSchemaFlow), qui reste inchangé.
 * Aucun cadre autour des nœuds : pictogramme monoligne, libellé, chiffre. Les liaisons sont des
 * pointillés animés dans le sens du courant, éteints quand rien ne circule.
 *
 * Topologie lue dans le schéma :
 *   compteur(s) → tableau → ports WattelEM (tableau.data.wattelemList) → zones (edge sourceHandle
 *   "wem-<sn>" ou zone.data.wattelemSn) → équipements de mesure (points).
 */

interface MeterData {
  deviceId: number;
  activeFPower: number | null;
  activeFEnergy: number | null;
  rmsVoltage?: number | null;
}

interface TelemetryData {
  power_kw?: string | number | null;
  energy_kwh?: string | number | null;
  voltage_v?: string | number | null;
  current_a?: string | number | null;
  voltage?: string | number | null;
  current?: string | number | null;
}

/** État de communication d'un appareil (mqtt_device_states), tel que remonté par la page. */
interface DeviceStateLike {
  deviceSn?: string | null;
  online?: boolean | null;
  lastSeen?: string | null;
}

interface SynopticViewProps {
  nodes: Node[];
  edges: Edge[];
  devices?: DeviceStateLike[];
  meterData?: MeterData[];
  wattelemTelemetry?: Record<string, TelemetryData>;
  installationName?: string;
  /** Intégré dans la page Consultation : pas de bandeau propre, s'insère dans le flux parent. */
  embedded?: boolean;
  onClose?: () => void;
  onTableauClick?: (tableauNode: Node) => void;
  onPointClick?: (pointNode: Node) => void;
}

// ─── Modèle dérivé du schéma ────────────────────────────────────────────────

type Model = 'go' | 'wall' | 'w7' | 'point';

interface Wem {
  sn: string;
  name: string;
  kw: number | null;
  v: number | null;
  a: number | null;
  kwh: number | null;
  online: boolean;
  lastSeen: string | null;
}

interface Eq {
  node: Node;
  name: string;
  sub: string;
  /** Aucun identifiant à afficher (sub = « non relié »). */
  noSub: boolean;
  model: Model;
  kw: number | null;
  v: number | null;
  linked: boolean;
  online: boolean;
  lastSeen: string | null;
  deducedKwh: number | null;
}

/** "il y a 3 min", "il y a 2 h", "il y a 5 j" — pour dater une perte de communication. */
function since(iso: string | null | undefined, t: TFn): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return t('arch_syn_ago_lt1min');
  if (s < 3600) return t('arch_syn_ago_min', { n: Math.round(s / 60) });
  if (s < 86400) return t('arch_syn_ago_h', { n: Math.round(s / 3600) });
  return t('arch_syn_ago_d', { n: Math.round(s / 86400) });
}

/** Famille de zone (pièce, cuisine, éclairage…) → pictogramme dans l'en-tête de zone. */
type ZoneKind =
  | 'piece' | 'cuisine' | 'eclairage' | 'chauffage' | 'clim' | 'prises' | 'bureau' | 'serveur'
  | 'parking' | 'wattel' | 'salon' | 'chambre' | 'sdb' | 'exterieur' | 'atelier' | 'accueil' | 'tableau' | 'inconnu';

const ZONE_KIND_KEY: Record<ZoneKind, TKey> = {
  piece: 'arch_zk_piece', cuisine: 'arch_zk_cuisine', eclairage: 'arch_zk_eclairage', chauffage: 'arch_zk_chauffage', clim: 'arch_zk_clim',
  prises: 'arch_zk_prises', bureau: 'arch_zk_bureau', serveur: 'arch_zk_serveur', parking: 'arch_zk_parking', wattel: 'arch_zk_wattel',
  salon: 'arch_zk_salon', chambre: 'arch_zk_chambre', sdb: 'arch_zk_sdb', exterieur: 'arch_zk_exterieur', atelier: 'arch_zk_atelier',
  accueil: 'arch_zk_accueil', tableau: 'arch_zk_tableau', inconnu: 'arch_zk_inconnu',
};

const ZONE_EMOJI: Array<[RegExp, ZoneKind]> = [
  [/⚡/u, 'wattel'], [/💡/u, 'eclairage'], [/🔥/u, 'chauffage'], [/❄/u, 'clim'], [/🔌/u, 'prises'],
  [/💼/u, 'bureau'], [/🍳/u, 'cuisine'], [/💻|🖥/u, 'serveur'], [/🚗|🅿/u, 'parking'], [/🛏/u, 'chambre'],
  [/🛋/u, 'salon'], [/🚿|🛁|🚽/u, 'sdb'], [/🌳|🌲|☀/u, 'exterieur'], [/🔧|🛠/u, 'atelier'], [/🚪/u, 'accueil'], [/🏠|🏡/u, 'piece'],
];
const ZONE_WORDS: Array<[RegExp, ZoneKind]> = [
  [/cuisine|électromén|electromen|kitchen/, 'cuisine'],
  [/[ée]clairage|lumi[èe]re|lighting/, 'eclairage'],
  [/chauff|radiateur|chaudi[èe]re|heating/, 'chauffage'],
  [/clim|cvc|ventil|hvac|froid/, 'clim'],
  [/prise|socket/, 'prises'],
  [/serveur|informatique|\bit\b|data|baie/, 'serveur'],
  [/bureau|office|open ?space|poste/, 'bureau'],
  [/parking|garage|voiture|véhicule|vehicule/, 'parking'],
  [/wattel|borne|recharge|ev\b/, 'wattel'],
  [/salon|s[ée]jour|salle [àa] manger|living/, 'salon'],
  [/chambre|dortoir|bedroom/, 'chambre'],
  [/bain|douche|\bwc\b|toilette|sanitaire/, 'sdb'],
  [/ext[ée]rieur|jardin|terrasse|cour\b|outdoor/, 'exterieur'],
  [/atelier|technique|local tech|workshop/, 'atelier'],
  [/accueil|entr[ée]e|hall|r[ée]ception/, 'accueil'],
  [/pi[èe]ce|room|zone/, 'piece'],
];

function zoneKind(data: Record<string, unknown> | undefined): ZoneKind {
  if (!data) return 'inconnu';
  const label = String(data.label ?? '').toLowerCase();
  const type = String(data.zoneType ?? '').toLowerCase();
  // 1. Nom explicite (le plus précis : "Cuisine" prime sur l'emoji générique ⚡ d'une "Zone Wattel")
  for (const [re, k] of ZONE_WORDS) if (re.test(label) && k !== 'piece') return k;
  // 2. Type choisi dans la bibliothèque de l'éditeur
  for (const [re, k] of ZONE_WORDS) if (type && re.test(type) && k !== 'piece') return k;
  // 3. Emoji persisté en base
  const icon = String(data.icon ?? '');
  for (const [re, k] of ZONE_EMOJI) if (re.test(icon)) return k;
  return /pi[èe]ce|room|zone/.test(label) ? 'piece' : 'inconnu';
}

/** Pictogrammes monolignes 24×24 (même trait que les autres icônes du synoptique). */
const ZONE_KIND_PATH: Record<ZoneKind, string> = {
  piece: 'M3 11 12 3l9 8M5 10v11h14V10M10 21v-6h4v6',
  cuisine: 'M4 13h12a1 1 0 0 1 0 2v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5zM16 14l5-3M7 9c0-2 2-2 2-4M11 9c0-2 2-2 2-4',
  eclairage: 'M9 18h6M10 21h4M12 3a6 6 0 0 1 3.6 10.8c-.6.5-.6 1.2-.6 2.2H9c0-1-0-1.7-.6-2.2A6 6 0 0 1 12 3z',
  chauffage: 'M12 3c1 3 4 4 4 8a4 4 0 0 1-8 0c0-2 1-3 1-3 0 2 1 3 2 3 0-3-1-5 1-8zM5 21h14',
  clim: 'M12 2v20M2 12h20M5 5l14 14M19 5 5 19M12 2l-2 2M12 2l2 2M12 22l-2-2M12 22l2-2',
  prises: 'M9 3v5M15 3v5M6 8h12v4a6 6 0 0 1-12 0V8zM12 18v3',
  bureau: 'M3 8h18v12H3zM9 8V5h6v3M3 13h18M11 13v2h2v-2',
  serveur: 'M4 4h16v6H4zM4 14h16v6H4zM8 7h.01M8 17h.01M12 7h6M12 17h6',
  parking: 'M5 17h14M6 17l1.5-6h9L18 17M6 17v3M18 17v3M8 11l1.5-4h5L16 11M9 14h.01M15 14h.01',
  wattel: 'M13 2 4 14h7l-1 8 9-12h-7z',
  salon: 'M4 12V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4M3 12h18v5H3zM5 17v2M19 17v2M8 12v-2h8v2',
  chambre: 'M3 18v-7h18v7M3 20v-2M21 20v-2M5 11V6h14v5M7 11V8h4v3M13 11V8h4v3',
  sdb: 'M12 3c-3 4-5 7-5 10a5 5 0 0 0 10 0c0-3-2-6-5-10z',
  exterieur: 'M12 22v-6M12 16l-5 0 3-4-3 0 5-9 5 9-3 0 3 4z',
  atelier: 'M14 7a4 4 0 0 0 4 4l-8 8a2 2 0 0 1-3-3l8-8a4 4 0 0 0-1-1zM17 4l3 3',
  accueil: 'M5 21V4a1 1 0 0 1 1-1h9v18M15 3l4 2v16M11 12h.01M5 21h14',
  tableau: 'M3 2h18v20H3zM7 7h4M13 7h4M7 11h4M13 11h4M7 15h4M13 15h4',
  inconnu: 'M3 11 12 3l9 8M5 10v11h14V10M10 21v-6h4v6',
};

interface ZoneSec {
  key: string;
  node: Node | null;
  name: string;
  kind: ZoneKind;
  wem: number; // index dans la liste des WattelEM du tableau, -1 si non relié
  eqs: Eq[];
}

interface Section {
  tableau: Node;
  name: string;
  meters: Node[];
  wems: Wem[];
  zones: ZoneSec[];
}

const METER_TYPES = new Set(['linky', 'edf', 'triphase', 'souscompteur', 'compteur']);
// Couleurs des WattelEM : variables CSS, redéfinies en thème nuit (voir SYNOPTIC_CSS)
const PAL = ['var(--zs-w1)', 'var(--zs-w2)', 'var(--zs-w3)', 'var(--zs-w4)', 'var(--zs-w5)', 'var(--zs-w6)', 'var(--zs-w7)', 'var(--zs-w8)'];
const NEUTRAL = 'var(--zs-neutral)';
const SIL: Record<Model, { src: string | null; w: number; h: number }> = {
  wall: { src: '/devices/wattelwall.svg', w: 88, h: 44 },
  go: { src: '/devices/wattelgo.svg', w: 28, h: 46 },
  w7: { src: '/devices/wattel7.svg', w: 30, h: 46 },
  point: { src: null, w: 44, h: 44 },
};
const W = 1000;
const LIMIT = 6;
const ZOOM_MIN = 0.25, ZOOM_MAX = 3, ZOOM_STEP = 0.25;
const ZOOM_KEY = 'zos-synoptic-zoom'; // nombre (échelle manuelle) ou 'fit' (ajusté à la fenêtre)
const VIEWPORT_PAD = 8;
const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Formateurs dépendant de la langue (séparateur décimal, groupement des milliers). */
function makeFormatters(locale: string) {
  const dec = (s: string) => (locale.startsWith('fr') ? s.replace('.', ',') : s);
  const fmtKw = (kw: number | null | undefined): string => {
    if (kw === null || kw === undefined) return '—';
    return dec(kw > 0 && kw < 0.01 ? kw.toFixed(3) : kw.toFixed(kw < 1 ? 2 : 1));
  };
  const fmtKwh = (v: number | null | undefined): string =>
    v === null || v === undefined ? '—' : v.toLocaleString(locale, { maximumFractionDigits: v < 10 ? 2 : 0 });
  const fmtA = (a: number | null): string => (a === null ? '— A' : `${dec(a.toFixed(2))} A`);
  return { fmtKw, fmtKwh, fmtA };
}
const fmtV = (v: number | null | undefined): string => (v === null || v === undefined ? '— V' : `${v.toFixed(0)} V`);
const shortSn = (sn: string): string => (sn.length > 16 ? `…${sn.slice(-12)}` : sn);
/** Version très courte pour les cases d'équipement (100 px) : les 6 derniers caractères. */
const tinySn = (sn: string): string => (sn.length > 8 ? `…${sn.slice(-6)}` : sn);
const ellipsis = (s: string, max: number): string => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

function guessModel(data: Record<string, unknown>): Model {
  const hint = `${data.pointType ?? ''} ${data.type ?? ''} ${data.deviceName ?? ''} ${data.label ?? ''}`.toLowerCase();
  if (/wattel\s*go|wattelgo|_g[a-z]?\b/.test(hint)) return 'go';
  if (/wattel\s*wall|wattelwall|_w[a-z]?\b/.test(hint)) return 'wall';
  if (/wattel\s*(7|22)|wattel7|wattel22|ocpp/.test(hint) || data.wattelDeviceType === 'ocpp') return 'w7';
  return 'point';
}

function buildSections(
  nodes: Node[],
  edges: Edge[],
  meterMap: Map<number, MeterData>,
  telemetry: Record<string, TelemetryData>,
  stateBySn: Map<string, DeviceStateLike>,
  t: TFn,
  tp: ReturnType<typeof useI18n>['tp'],
): Section[] {
  const byId = new Map(nodes.map(n => [n.id, n]));
  const tableaux = nodes.filter(n => n.type === 'tableau');
  const meters = nodes.filter(n => METER_TYPES.has(String(n.type)) || n.data?.type === 'Compteur');
  const zoneNodes = nodes.filter(n => n.type === 'zone');
  const pointNodes = nodes.filter(n => n.type === 'point');

  const toEq = (p: Node): Eq => {
    const d = (p.data ?? {}) as Record<string, unknown>;
    const deviceId = num(d.deviceId);
    const deviceSn = typeof d.deviceSn === 'string' && d.deviceSn ? d.deviceSn : null;
    const linked = Boolean(deviceId || deviceSn);
    let kw: number | null = null;
    let v: number | null = null;
    let hasData = false;
    if (deviceId && meterMap.has(deviceId)) {
      const m = meterMap.get(deviceId)!;
      kw = (num(m.activeFPower) ?? 0) / 10_000_000;
      v = num(m.rmsVoltage);
      hasData = true;
    } else if (deviceSn && telemetry[deviceSn]) {
      const t = telemetry[deviceSn];
      kw = num(t.power_kw) ?? 0;
      v = num(t.voltage_v) ?? num(t.voltage);
      hasData = true;
    } else if (d.meterPower !== undefined && d.meterPower !== null) {
      kw = (num(d.meterPower) ?? 0) / 10_000_000;
      v = num(d.meterVoltage);
      hasData = linked;
    }
    const deduced = num(d.deducedEnergy);
    // État de communication : l'état d'appareil (MQTT) prime, sinon la présence de données, sinon le statut du nœud.
    const st = deviceSn ? stateBySn.get(deviceSn) : undefined;
    let online: boolean;
    if (!linked) online = true;
    else if (st && typeof st.online === 'boolean') online = st.online;
    else online = hasData || d.status === 'online';
    const noSub = !deviceSn && !(typeof d.deviceName === 'string' && d.deviceName) && !linked;
    const sub = deviceSn ? shortSn(deviceSn) : typeof d.deviceName === 'string' && d.deviceName ? d.deviceName : linked ? `#${deviceId}` : t('arch_syn_unlinked_sub');
    return {
      node: p,
      name: displayDefaultName(String(d.label ?? d.deviceName ?? t('arch_syn_device')), t, tp),
      sub,
      noSub,
      model: guessModel(d),
      kw: linked ? (hasData ? kw : null) : null,
      v,
      linked,
      online,
      lastSeen: st?.lastSeen ?? null,
      deducedKwh: !linked && deduced && deduced > 0 ? deduced / 1000 : null,
    };
  };

  const childrenOf = (id: string) => edges.filter(e => e.source === id).map(e => byId.get(e.target)).filter((n): n is Node => Boolean(n));
  const usedZones = new Set<string>();
  const usedPoints = new Set<string>();
  const usedMeters = new Set<string>();

  const sections: Section[] = tableaux.map((tb, ti) => {
    const list = (Array.isArray(tb.data?.wattelemList) ? tb.data.wattelemList : []) as Array<Record<string, unknown>>;
    const wems: Wem[] = list
      .filter(w => typeof w.sn === 'string' && w.sn)
      .map(w => {
        const sn = String(w.sn);
        const tel = telemetry[sn];
        const st = stateBySn.get(sn);
        return {
          sn,
          name: String(w.name ?? sn),
          kw: tel ? num(tel.power_kw) ?? 0 : num(w.power),
          v: tel ? num(tel.voltage_v) ?? num(tel.voltage) : num(w.voltage),
          a: tel ? num(tel.current_a) ?? num(tel.current) : num(w.current),
          kwh: tel ? num(tel.energy_kwh) : num(w.energy),
          online: st && typeof st.online === 'boolean' ? st.online : Boolean(tel) || w.online === true,
          lastSeen: st?.lastSeen ?? null,
        };
      });
    const snIndex = new Map(wems.map((w, i) => [w.sn, i]));

    const secMeters = meters.filter(m => edges.some(e => e.source === m.id && e.target === tb.id));
    secMeters.forEach(m => usedMeters.add(m.id));

    const zones: ZoneSec[] = [];
    const directEqs: Eq[] = [];
    edges.filter(e => e.source === tb.id).forEach(e => {
      const target = byId.get(e.target);
      if (!target) return;
      if (target.type === 'zone' && !usedZones.has(target.id)) {
        usedZones.add(target.id);
        const handleSn = e.sourceHandle?.startsWith('wem-') ? e.sourceHandle.slice(4) : null;
        const sn = handleSn ?? (typeof target.data?.wattelemSn === 'string' ? target.data.wattelemSn : null);
        const wem = sn !== null && snIndex.has(sn) ? snIndex.get(sn)! : -1;
        const eqs = childrenOf(target.id).filter(n => n.type === 'point').map(p => { usedPoints.add(p.id); return toEq(p); });
        zones.push({ key: target.id, node: target, name: displayDefaultName(String(target.data?.label ?? t('arch_syn_zone')), t, tp), kind: zoneKind(target.data), wem, eqs });
      } else if (target.type === 'point' && !usedPoints.has(target.id)) {
        usedPoints.add(target.id);
        directEqs.push(toEq(target));
      }
    });
    if (directEqs.length) zones.push({ key: `${tb.id}-direct`, node: null, name: t('arch_syn_direct'), kind: 'tableau', wem: -1, eqs: directEqs });
    return { tableau: tb, name: tb.data?.label ? tableauDisplayName(String(tb.data.label), t, tp) : t('arch_syn_panel_n', { n: ti + 1 }), meters: secMeters, wems, zones };
  });

  // Éléments non reliés : rattachés à la première section pour rester visibles
  if (sections.length) {
    const first = sections[0];
    const orphanMeters = meters.filter(m => !usedMeters.has(m.id));
    if (!first.meters.length) first.meters = orphanMeters;
    zoneNodes.filter(z => !usedZones.has(z.id)).forEach(z => {
      const eqs = childrenOf(z.id).filter(n => n.type === 'point').map(p => { usedPoints.add(p.id); return toEq(p); });
      const sn = typeof z.data?.wattelemSn === 'string' ? z.data.wattelemSn : null;
      const wem = sn ? first.wems.findIndex(w => w.sn === sn) : -1;
      first.zones.push({ key: z.id, node: z, name: displayDefaultName(String(z.data?.label ?? t('arch_syn_zone')), t, tp), kind: zoneKind(z.data), wem, eqs });
    });
    const orphanPoints = pointNodes.filter(p => !usedPoints.has(p.id)).map(toEq);
    if (orphanPoints.length) first.zones.push({ key: 'orphans', node: null, name: t('arch_syn_unlinked_group'), kind: 'inconnu', wem: -1, eqs: orphanPoints });
  }
  return sections;
}

// ─── Composant ──────────────────────────────────────────────────────────────

export function SynopticView({
  nodes,
  edges,
  devices = [],
  meterData = [],
  wattelemTelemetry = {},
  installationName,
  embedded = false,
  onClose,
  onTableauClick,
  onPointClick,
}: SynopticViewProps) {
  const { t, tp, locale } = useI18n();
  const name = installationName || t('arch_syn_default_name');
  const { fmtKw, fmtKwh, fmtA } = useMemo(() => makeFormatters(locale), [locale]);
  const zkLabel = (k: ZoneKind) => t(ZONE_KIND_KEY[k]);
  const meterMap = useMemo(() => new Map(meterData.map(m => [Number(m.deviceId), m])), [meterData]);
  const stateBySn = useMemo(() => {
    const m = new Map<string, DeviceStateLike>();
    devices.forEach(d => { if (d && typeof d.deviceSn === 'string' && d.deviceSn) m.set(d.deviceSn, d); });
    return m;
  }, [devices]);
  const sections = useMemo(() => buildSections(nodes, edges, meterMap, wattelemTelemetry, stateBySn, t, tp), [nodes, edges, meterMap, wattelemTelemetry, stateBySn, t, tp]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [detail, setDetail] = useState<{ key: string; title: string; rows: Array<[string, number | null]> } | null>(null);
  // Le contenu de la fiche est figé à l'ouverture : on la ferme au changement de langue
  useEffect(() => { setDetail(null); }, [locale]);
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Zoom : boutons, pincement à deux doigts, Ctrl+molette. ──
  // 1 = pleine largeur de la fenêtre. « Ajuster » (mode fit, par défaut) calcule l'échelle pour que
  // tout le schéma tienne dans la fenêtre, en largeur ET en hauteur, et suit les redimensionnements.
  const [manualZoom, setManualZoom] = useState<number>(1);
  const [fitMode, setFitMode] = useState<boolean>(true);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(ZOOM_KEY);
      const v = Number(raw);
      if (raw && raw !== 'fit' && v >= ZOOM_MIN && v <= ZOOM_MAX) { setManualZoom(v); setFitMode(false); }
    } catch { /* stockage indisponible */ }
  }, []);
  const viewportRef = useRef<HTMLDivElement>(null);
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);
  const [vpSize, setVpSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => {
      const r = entries[0]?.contentRect;
      if (r) setVpSize({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const applyZoom = useCallback((next: number) => {
    const z = clampZoom(next);
    setManualZoom(z);
    setFitMode(false);
    try { localStorage.setItem(ZOOM_KEY, String(z)); } catch { /* stockage indisponible */ }
  }, []);
  const fitToScreen = useCallback(() => {
    setFitMode(true);
    try { localStorage.setItem(ZOOM_KEY, 'fit'); } catch { /* stockage indisponible */ }
  }, []);
  const zoomRef = useRef(1); // échelle courante, lue par les gestes (calculée plus bas dans le rendu)
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const dist = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onStart = (e: TouchEvent) => { if (e.touches.length === 2) pinchRef.current = { dist: dist(e.touches), zoom: zoomRef.current }; };
    const onMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !pinchRef.current) return;
      if (e.cancelable) e.preventDefault();
      applyZoom(pinchRef.current.zoom * (dist(e.touches) / pinchRef.current.dist));
    };
    const onEnd = () => { pinchRef.current = null; };
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      applyZoom(zoomRef.current * (e.deltaY < 0 ? 1.1 : 0.9));
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('wheel', onWheel);
    };
  }, [applyZoom]);

  const toggleDetail = (key: string, title: string, rows: Array<[string, number | null]>) =>
    setDetail(prev => (prev?.key === key ? null : { key, title, rows }));

  // ── Statistiques ──
  const stats = useMemo(() => {
    let online = 0, off = 0, unlinked = 0, totalEqKw = 0;
    const offNames: string[] = [];
    let wemCount = 0, wemOff = 0, totalKw = 0, totalKwh = 0, anyKwh = false;
    let voltage: number | null = null;
    sections.forEach(s => {
      s.wems.forEach(w => {
        wemCount++;
        if (!w.online) wemOff++;
        if (w.online) totalKw += w.kw ?? 0; // hors ligne : dernière valeur périmée, non comptée
        if (w.kwh !== null) { totalKwh += w.kwh; anyKwh = true; }
        if (voltage === null && w.v !== null) voltage = w.v;
      });
      s.zones.forEach(z => z.eqs.forEach(e => {
        if (!e.linked) { unlinked++; return; }
        if (e.online) { online++; totalEqKw += e.kw ?? 0; } else { off++; offNames.push(e.name); }
      }));
    });
    if (!wemCount) totalKw = totalEqKw;
    return { online, off, unlinked, totalEqKw, offNames, wemCount, wemOff, totalKw, totalKwh: anyKwh ? totalKwh : null, voltage, issues: off + wemOff };
  }, [sections]);

  // ── Génération SVG ──
  const { elements, height } = useMemo(() => {
    const out: ReactNode[] = [];
    let y = 0;

    sections.forEach((sec, si) => {
      const y0 = y + 64;
      const meterX = 120, hubX = 500;
      const nW = sec.wems.length;
      // totaux : appareils en ligne uniquement (la dernière valeur d'un appareil hors ligne est périmée)
      const totW = nW ? sec.wems.reduce((a, w) => a + (w.online ? w.kw ?? 0 : 0), 0) : sec.zones.reduce((a, z) => a + z.eqs.reduce((b, e) => b + (e.online ? e.kw ?? 0 : 0), 0), 0);
      const key = `s${si}`;

      // Compteur(s)
      const meter = sec.meters[0];
      if (meter) {
        const d = (meter.data ?? {}) as Record<string, unknown>;
        // « Compteur » est le type par défaut stocké dans le nœud : affiché dans la langue courante
        const typeLabel = d.type === 'Compteur' ? t('arch_syn_meter') : d.type ? String(d.type) : null;
        const label = d.label != null ? displayDefaultName(String(d.label), t, tp) : typeLabel ?? t('arch_syn_meter');
        const subParts = [typeLabel && typeLabel !== label ? typeLabel : null, d.power ? String(d.power) : null, sec.meters.length > 1 ? tp('arch_syn_more_meters', sec.meters.length - 1) : null].filter(Boolean);
        const meterTitle = `${label} · ${t('arch_syn_delivery_point')}`;
        const meterRows: Array<[string, number | null]> = [[t('arch_syn_import'), totW], [t('arch_syn_export'), 0]];
        out.push(
          <g key={`${key}-meter`}>
            <use href="#zs-ic-meter" x={meterX - 22} y={y0 - 22} width={44} height={44} className="zs-ico zs-accent" />
            <text className="zs-lbl" x={meterX + 30} y={y0 - 4}>{label}</text>
            <text className="zs-sub" x={meterX + 30} y={y0 + 12}>{subParts.join(' · ') || t('arch_syn_delivery_point')}</text>
            <text className="zs-val" x={meterX - 22} y={y0 + 62}>{fmtKw(totW)}<tspan className="zs-u"> kW</tspan></text>
            <path className="zs-wire" d={`M${meterX + 150},${y0} H${hubX - 40}`} />
            <path className={`zs-flow${totW > 0 ? (totW > 5 ? ' zs-fast' : '') : ' zs-idle'}`} stroke="var(--zs-blue)" d={`M${meterX + 150},${y0} H${hubX - 42}`} />
            <path fill="var(--zs-blue)" d={`M${hubX - 44},${y0 - 5} l8,5 -8,5z`} />
            <rect className="zs-hit" x={meterX - 30} y={y0 - 30} width={200} height={100} rx={10} tabIndex={0} role="button" aria-label={t('arch_syn_aria_detail', { name: label })}
              onClick={() => toggleDetail(`${key}-meter`, meterTitle, meterRows)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDetail(`${key}-meter`, meterTitle, meterRows); } }} />
          </g>,
        );
      }

      // Tableau (hub) : le boîtier montre ses modules WattelEM alignés (dessin d'après le produit),
      // au plus 4, puis « +N ». Sans WattelEM, on garde le pictogramme de tableau.
      const shownMods = Math.min(nW, 4);
      const MOD_W = 37, MOD_H = 62, MOD_GAP = 4;
      const boxW = nW ? 10 + shownMods * (MOD_W + MOD_GAP) + (nW > 4 ? 30 : 0) : 0;
      const boxX = hubX - 36;
      const textX = nW ? boxX + boxW + 14 : hubX + 34;
      const trunkX = nW ? boxX + boxW / 2 : hubX;
      out.push(
        <g key={`${key}-hub`}>
          {nW ? (
            <>
              <rect x={boxX} y={y0 - 34} width={boxW} height={74} rx={8} fill="var(--zs-tile)" stroke="var(--zs-line)" />
              {sec.wems.slice(0, shownMods).map((w, i) => (
                <use key={w.sn} href="#zs-wem" x={boxX + 5 + i * (MOD_W + MOD_GAP)} y={y0 - 28} width={MOD_W} height={MOD_H} opacity={w.online ? 1 : 0.45} />
              ))}
              {nW > 4 && <text className="zs-sub" x={boxX + boxW - 15} y={y0 + 8} textAnchor="middle">+{nW - 4}</text>}
            </>
          ) : (
            <use href="#zs-ic-hub" x={hubX - 26} y={y0 - 30} width={52} height={52} className="zs-ico zs-hub" />
          )}
          <text className="zs-lbl" x={textX} y={y0 - 6}>{sec.name}</text>
          <text className="zs-sub" x={textX} y={y0 + 10}>{nW ? tp('arch_syn_hub_wems', nW) : t('arch_syn_no_wem')}</text>
          <text className="zs-hubval" x={textX} y={y0 + 36}>{fmtKw(totW)}<tspan className="zs-u2"> kW</tspan></text>
          {/* Zone du tableau (nom + puissance) : ouvre tous les WattelEM */}
          <rect className="zs-hit" x={nW ? boxX + boxW + 4 : hubX - 30} y={y0 - 38} width={nW ? 226 : 190} height={82} rx={10} tabIndex={0} role="button" aria-label={t('arch_syn_aria_open_devices', { name: sec.name })}
            onClick={() => onTableauClick?.(sec.tableau)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTableauClick?.(sec.tableau); } }} />
          {/* Chaque module WattelEM ouvre SA page, comme sa pastille EMn */}
          {sec.wems.slice(0, shownMods).map((w, i) => {
            const open = () => onTableauClick?.({ ...sec.tableau, data: { ...sec.tableau.data, wattelemList: [{ sn: w.sn, name: w.name }] } });
            return (
              <g key={`hit-${w.sn}`}>
                <title>{`WattelEM ${w.sn} · ${w.online ? t('arch_syn_online_lc') : t('arch_syn_offline_lc')} · ${fmtKw(w.kw)} kW`}</title>
                <rect className="zs-hit" x={boxX + 4 + i * (MOD_W + MOD_GAP)} y={y0 - 31} width={MOD_W + 2} height={68} rx={6} tabIndex={0} role="button" aria-label={`WattelEM ${w.sn}`}
                  onClick={open} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }} />
              </g>
            );
          })}
          {nW > 4 && <rect className="zs-hit" x={boxX + boxW - 30} y={y0 - 31} width={28} height={68} rx={6} tabIndex={0} role="button" aria-label={t('arch_syn_all_wems')}
            onClick={() => onTableauClick?.(sec.tableau)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTableauClick?.(sec.tableau); } }} />}
        </g>,
      );

      // Ports WattelEM sous le tableau
      const portY = y0 + 86;
      const spacing = Math.min(76, (W - 260) / Math.max(nW, 1));
      const px = (i: number) => hubX + (i - (nW - 1) / 2) * spacing;
      const wc = (i: number) => (i >= 0 ? PAL[i % PAL.length] : NEUTRAL);
      if (nW) {
        out.push(<path key={`${key}-trunk`} className="zs-wire" d={`M${trunkX},${y0 + 40} V${portY - 26} H${hubX}`} />);
        if (nW > 1) out.push(<path key={`${key}-bus`} className="zs-wire" d={`M${px(0)},${portY - 26} H${px(nW - 1)}`} />);
        sec.wems.forEach((w, i) => {
          const x = px(i), c = wc(i);
          const open = () => onTableauClick?.({ ...sec.tableau, data: { ...sec.tableau.data, wattelemList: [{ sn: w.sn, name: w.name }] } });
          out.push(
            <g key={`${key}-wem${i}`} className={w.online ? undefined : 'zs-off'}>
              <path className="zs-wire" d={`M${x},${portY - 26} V${portY - 14}`} />
              <rect x={x - 26} y={portY - 13} width={52} height={26} rx={13} fill={c} />
              <use href="#zs-ic-wem" className="zs-pico" x={x - 21} y={portY - 8} width={16} height={16} />
              <text className="zs-port" x={x + 8} y={portY + 3.5} textAnchor="middle">EM{i + 1}</text>
              {w.online
                ? <text className="zs-portkw" x={x} y={portY + 30} textAnchor="middle">{fmtKw(w.kw)}</text>
                : <text className="zs-portkw zs-portoff" x={x} y={portY + 30} textAnchor="middle">{t('arch_syn_chip_offline')}</text>}
              <circle className={w.online ? 'zs-dot zs-dot-ok zs-live' : 'zs-dot zs-dot-err'} cx={x + 24} cy={portY - 12} r={4.5} />
              <title>{`WattelEM ${w.sn} · ${w.online ? t('arch_syn_online_lc') : `${t('arch_syn_offline_lc')}${since(w.lastSeen, t) ? ` · ${t('arch_syn_seen', { ago: since(w.lastSeen, t)! })}` : ''}`} · ${fmtV(w.v)} · ${fmtA(w.a)}`}</title>
              <rect className="zs-hit" x={x - 30} y={portY - 22} width={64} height={62} rx={10} tabIndex={0} role="button" aria-label={`WattelEM ${w.sn}`}
                onClick={open} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }} />
            </g>,
          );
        });
      }

      // Zones en colonnes
      const nZ = sec.zones.length;
      const cols = nZ <= 1 ? 1 : Math.min(3, nZ);
      const colW = (W - 40) / cols, cellW = 100, cellH = 146;
      const zy0 = (nW ? portY : y0 + 40) + 82;
      const rowsCount = Math.ceil(nZ / cols);
      const heights = sec.zones.map(z => {
        const shown = expanded[z.key] ? z.eqs.length : Math.min(z.eqs.length, LIMIT);
        const extra = z.eqs.length > LIMIT ? 1 : 0;
        const per = Math.max(2, Math.floor((colW - 40) / cellW));
        const r = Math.max(1, Math.ceil((shown + extra) / per));
        return 40 + (z.eqs.length ? r * cellH : 24) + 12;
      });
      const rowY: number[] = [];
      let yy = zy0;
      for (let r = 0; r < rowsCount; r++) {
        rowY.push(yy);
        let h = 0;
        for (let c = 0; c < cols; c++) { const zi = r * cols + c; if (zi < nZ) h = Math.max(h, heights[zi]); }
        yy += h + 26;
      }
      if (!nZ) {
        out.push(<text key={`${key}-empty`} className="zs-sub" x={hubX} y={zy0} textAnchor="middle">{t('arch_syn_no_zone')}</text>);
        yy = zy0 + 20;
      }

      sec.zones.forEach((z, zi) => {
        const col = zi % cols, row = Math.floor(zi / cols);
        const zx = 20 + col * colW, zy = rowY[row], zw = colW, c = wc(z.wem), anchorX = zx + zw / 2;
        const ztot = z.eqs.reduce((a, e) => a + (e.online ? e.kw ?? 0 : 0), 0);
        const wemLabel = z.wem >= 0 ? `EM${z.wem + 1}` : null;
        const parts: ReactNode[] = [];
        if (row === 0 && z.wem >= 0 && nW) {
          const laneY = portY + 34 + z.wem * 4, x0 = px(z.wem);
          parts.push(<path key="lk" className={`zs-flow${ztot > 0 ? '' : ' zs-idle'}`} stroke={c} d={`M${x0},${portY + 14} V${laneY} H${anchorX} V${zy - 6}`} />);
        } else {
          parts.push(<path key="lk" className={`zs-flow${ztot > 0 ? '' : ' zs-idle'}`} stroke={c} d={`M${anchorX},${zy - 22} V${zy - 6}`} />);
        }
        parts.push(<path key="arr" fill={c} d={`M${anchorX - 5},${zy - 8} l5,8 5,-8z`} />);
        const badgeW = wemLabel ? 46 : 0;
        if (wemLabel) {
          parts.push(
            <g key="badge">
              <rect x={zx + 12} y={zy + 6} width={46} height={20} rx={10} fill={c} />
              <use href="#zs-ic-wem" className="zs-pico" x={zx + 16} y={zy + 9} width={14} height={14} />
              <text className="zs-badge" x={zx + 43} y={zy + 19.5} textAnchor="middle">{wemLabel}</text>
            </g>,
          );
        }
        // Pictogramme du type de zone (pièce, cuisine, éclairage…) dans la couleur de la zone
        const iconX = zx + 20 + badgeW;
        parts.push(
          <g key="zk" className="zs-zk" transform={`translate(${iconX}, ${zy + 5}) scale(0.9)`}>
            <title>{zkLabel(z.kind)}</title>
            <path d={ZONE_KIND_PATH[z.kind]} stroke={c} />
          </g>,
        );
        // Nom + type écrit en clair (« Zone 1 · Pièce »), sauf si le nom le contient déjà
        const kindLabel = zkLabel(z.kind);
        const kindWord = kindLabel.split(/[\s/]/)[0].toLowerCase();
        const showKind = z.kind !== 'inconnu' && z.node !== null && !z.name.toLowerCase().includes(kindWord);
        parts.push(
          <text key="zn" className="zs-zone" x={iconX + 28} y={zy + 20}>
            {z.name}
            {showKind && <tspan className="zs-zkind"> · {kindLabel}</tspan>}
          </text>,
        );
        parts.push(<text key="zt" className="zs-ztot" x={zx + zw - 14} y={zy + 20} textAnchor="end">{tp('arch_syn_eq_count', z.eqs.length)} · {fmtKw(ztot)} kW</text>);
        parts.push(<path key="ln" className="zs-lane" stroke={c} d={`M${zx + 12},${zy + 32} H${zx + zw - 12}`} />);
        const zoneDetail = () => toggleDetail(
          `${key}-${z.key}`,
          `${z.name} · ${zkLabel(z.kind)}${z.wem >= 0 ? ` · ${t('arch_syn_measured_by', { n: z.wem + 1, sn: sec.wems[z.wem].sn })}` : ''}`,
          z.eqs.map(e => [`${e.name} (${e.sub})${e.linked && !e.online ? ` · ${t('arch_syn_offline_lc')}` : ''}`, e.kw]),
        );
        parts.push(<rect key="zh" className="zs-hit" x={zx + 8} y={zy} width={zw - 16} height={34} rx={10} tabIndex={0} role="button" aria-label={t('arch_syn_aria_breakdown', { name: z.name })}
          onClick={zoneDetail} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); zoneDetail(); } }} />);

        const per = Math.max(2, Math.floor((zw - 40) / cellW));
        const startX = zx + (zw - per * cellW) / 2;
        const list = expanded[z.key] ? z.eqs : z.eqs.slice(0, LIMIT);
        list.forEach((e, k) => {
          const cx = startX + (k % per) * cellW + cellW / 2, cy = zy + 46 + Math.floor(k / per) * cellH;
          const sz = SIL[e.model];
          const dim = e.linked && !e.online;
          // Deux lignes courtes (la case fait 100 px) : identifiant, puis tension ou dernière vue
          const snTiny = e.noSub ? '' : (e.sub.startsWith('…') ? tinySn(e.sub.slice(1)) : ellipsis(e.sub, 14));
          const meta1 = !e.linked ? (e.deducedKwh !== null ? t('arch_syn_calc_energy') : t('arch_syn_no_device')) : snTiny;
          const ago = since(e.lastSeen, t);
          const meta2 = !e.linked ? '' : e.online ? fmtV(e.v) : (ago ? t('arch_syn_seen', { ago }) : t('arch_syn_no_news'));
          // Pastille d'état de communication (façon IHM industrielle) : EN LIGNE / HORS LIGNE / NON RELIÉ
          const status: 'ok' | 'off' | 'na' = !e.linked ? 'na' : e.online ? 'ok' : 'off';
          const statusLabel = status === 'ok' ? t('arch_syn_chip_online') : status === 'off' ? t('arch_syn_chip_offline') : t('arch_syn_chip_unlinked');
          const chipW = status === 'off' ? 70 : status === 'ok' ? 62 : 64;
          const go = () => onPointClick?.(e.node);
          parts.push(
            <g key={`eq-${e.node.id}`} className={dim ? 'zs-off' : undefined}>
              {sz.src
                ? <image className="zs-sil" href={sz.src} x={cx - sz.w / 2} y={cy + (48 - sz.h) / 2} width={sz.w} height={sz.h} />
                : <use className="zs-sil" href="#zs-dev-point" x={cx - sz.w / 2} y={cy + (48 - sz.h) / 2} width={sz.w} height={sz.h} />}
              <g className={`zs-chip-svg zs-st-${status}`}>
                <rect x={cx - chipW / 2} y={cy + 54} width={chipW} height={15} rx={7.5} />
                <circle className={status === 'ok' ? 'zs-live' : undefined} cx={cx - chipW / 2 + 9} cy={cy + 61.5} r={3} />
                <text x={cx - chipW / 2 + 16} y={cy + 65}>{statusLabel}</text>
              </g>
              <text className="zs-eq" x={cx} y={cy + 84} textAnchor="middle">{ellipsis(e.name, 14)}<title>{e.name}</title></text>
              <text className="zs-eqmeta" x={cx} y={cy + 97} textAnchor="middle">{meta1}</text>
              {meta2 && <text className={`zs-eqmeta${!e.online ? ' zs-eqmeta-off' : ''}`} x={cx} y={cy + 109} textAnchor="middle">{meta2}</text>}
              {!e.linked && e.deducedKwh !== null
                ? <text className="zs-eqkw" x={cx} y={cy + 129} textAnchor="middle">≈{fmtKwh(e.deducedKwh)}<tspan className="zs-u"> kWh</tspan></text>
                : <text className="zs-eqkw" x={cx} y={cy + 129} textAnchor="middle">{fmtKw(e.kw)}<tspan className="zs-u"> kW</tspan></text>}
              <rect className="zs-hit" x={cx - cellW / 2 + 4} y={cy - 4} width={cellW - 8} height={cellH - 8} rx={10} tabIndex={0} role="button" aria-label={t('arch_syn_aria_open_devices', { name: e.name })}
                onClick={go} onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(); } }} />
            </g>,
          );
        });
        if (z.eqs.length > LIMIT) {
          const k = list.length, cx2 = startX + (k % per) * cellW + cellW / 2, cy2 = zy + 46 + Math.floor(k / per) * cellH;
          const toggle = () => setExpanded(prev => ({ ...prev, [z.key]: !prev[z.key] }));
          parts.push(
            <g key="more">
              <rect x={cx2 - 42} y={cy2 + 20} width={84} height={44} rx={10} fill="none" stroke="var(--zs-blue)" strokeDasharray="4 4" />
              <text className="zs-more" x={cx2} y={cy2 + 46} textAnchor="middle">{expanded[z.key] ? t('arch_syn_collapse') : t('arch_syn_more_others', { n: z.eqs.length - LIMIT })}</text>
              <rect className="zs-hit" x={cx2 - 46} y={cy2 + 14} width={92} height={56} rx={10} tabIndex={0} role="button" aria-label={t('arch_syn_show_others')}
                onClick={toggle} onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); toggle(); } }} />
            </g>,
          );
        }
        if (!z.eqs.length) parts.push(<text key="none" className="zs-sub" x={anchorX} y={zy + 56} textAnchor="middle">{t('arch_syn_no_equipment')}</text>);
        out.push(<g key={`${key}-${z.key}`}>{parts}</g>);
      });

      y = yy;
      if (si < sections.length - 1) {
        out.push(<path key={`${key}-sep`} className="zs-wire" strokeDasharray="2 6" d={`M40,${y + 6} H${W - 40}`} />);
        y += 20;
      }
    });

    // Légende
    const H = y + 6;
    if (stats.wemCount) {
      out.push(
        <g key="legend">
          <rect x={20} y={H - 4} width={30} height={16} rx={8} fill="var(--zs-navy)" />
          <use href="#zs-ic-wem" className="zs-pico" x={24} y={H - 2} width={12} height={12} />
          <text className="zs-badge" x={43} y={H + 8} textAnchor="middle">EM</text>
          <text className="zs-legend" x={56} y={H + 8}>{t('arch_syn_legend')}</text>
        </g>,
      );
    }
    return { elements: out, height: H + 26 };
    // minuteTick : rafraîchit les « vu il y a X min » chaque minute
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, expanded, onTableauClick, onPointClick, stats.wemCount, t, tp, fmtKw, fmtKwh, fmtA, Math.floor(clock.getTime() / 60000)]);

  const empty = sections.length === 0;
  const detailMax = detail ? Math.max(...detail.rows.map(r => r[1] ?? 0), 0) || 1 : 1;

  // Échelle « ajustée » : le schéma entier tient dans la fenêtre (largeur et hauteur)
  const innerW = Math.max(0, vpSize.w - 2 * VIEWPORT_PAD), innerH = Math.max(0, vpSize.h - 2 * VIEWPORT_PAD);
  const fitScale = innerW > 0 && innerH > 0 ? Math.min(1, (innerH / innerW) / (height / W)) : 1;
  const zoom = fitMode ? Math.max(ZOOM_MIN, Math.round(fitScale * 100) / 100) : manualZoom;
  zoomRef.current = zoom;
  const svgWidth = innerW > 0 ? Math.round(innerW * zoom) : undefined;

  return (
    <div className={`zs-root ${embedded ? 'zs-embedded' : 'min-h-screen'}`}>
      <style>{SYNOPTIC_CSS}</style>

      {/* Bandeau (uniquement en plein écran) */}
      {!embedded && (
      <header className="zs-header bg-zce-navy text-white">
        <div className="max-w-[1160px] mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-2 text-sm font-semibold transition-colors">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              {t('arch_syn_back')}
            </button>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">{t('arch_synoptic_title')}</div>
              <div className="font-heading font-bold text-base truncate">{name}</div>
            </div>
          </div>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg bg-zce-orange hover:bg-orange-400 text-zce-navy px-3 py-2 text-sm font-semibold transition-colors">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V3h12v6M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v7H6z" /></svg>
            {t('arch_syn_print')}
          </button>
        </div>
      </header>
      )}

      <main className="max-w-[1160px] mx-auto px-4 py-4">
        <div className="zs-screen">
          {/* Barre d'état */}
          <div className="zs-status">
            <div className="zs-status-l">
              <span className="zs-sig" aria-hidden="true"><i style={{ height: 4 }} /><i style={{ height: 7 }} /><i style={{ height: 10 }} /><i style={{ height: 12 }} /></span>
              <span className="zs-on"><b className={stats.wemCount && stats.wemOff === stats.wemCount ? 'zs-off-dot' : undefined} />{stats.wemCount && stats.wemOff === stats.wemCount ? t('arch_offline') : t('arch_online')}</span>
              <span className="zs-name">{name}</span>
            </div>
            <div className="zs-status-r">
              <span className="zs-pill">{t('arch_tab_synoptic')}</span>
              <span className="zs-num">{clock.toLocaleDateString(locale)} {clock.toLocaleTimeString(locale)}</span>
            </div>
          </div>

          {/* Canvas */}
          <div className="zs-canvas">
            {empty ? (
              <div className="zs-empty">
                <svg viewBox="0 0 24 24" className="zs-ico" width={40} height={40}><use href="#zs-ic-hub" /></svg>
                <p>{t('arch_syn_empty')}</p>
                <p className="zs-sub-p">{t('arch_syn_empty_hint')}</p>
              </div>
            ) : (
              <div className={`zs-viewport ${embedded ? 'zs-vp-embedded' : 'zs-vp-full'}`} ref={viewportRef}>
              <div className="zs-stage">
              <svg className="zs-syn" style={svgWidth ? { width: svgWidth } : undefined} viewBox={`0 0 ${W} ${height}`} role="img" aria-label={t('arch_syn_aria_svg', { name })}>
                <defs>
                  <symbol id="zs-ic-meter" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M7 8h10M7 12h5M7 16h7" /><circle cx="16.5" cy="15.5" r="1.2" /></symbol>
                  <symbol id="zs-ic-hub" viewBox="0 0 24 24"><rect x="3" y="2" width="18" height="20" rx="2" /><path d="M7 7h4M13 7h4M7 11h4M13 11h4M7 15h4M13 15h4" /><rect x="9" y="18" width="6" height="1.5" rx=".75" /></symbol>
                  <symbol id="zs-ic-wem" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2.5" /><path d="M7 14c1.2-3.5 2.4-3.5 3.6 0s2.4 3.5 3.6 0 2.4-3.5 3.6 0" /><circle cx="7" cy="8" r="1" /></symbol>
                  {/* Module WattelEM (rail DIN, écran, boutons), dessiné d'après le produit */}
                  <symbol id="zs-wem" viewBox="0 0 120 200">
                    <rect x="14" y="2" width="92" height="22" rx="3" fill="#d9dee6" /><rect x="26" y="6" width="14" height="10" rx="1.5" fill="#8a94a6" /><rect x="80" y="6" width="14" height="10" rx="1.5" fill="#8a94a6" />
                    <rect x="14" y="176" width="92" height="22" rx="3" fill="#d9dee6" /><rect x="26" y="184" width="14" height="10" rx="1.5" fill="#8a94a6" /><rect x="80" y="184" width="14" height="10" rx="1.5" fill="#8a94a6" />
                    <rect x="6" y="20" width="108" height="160" rx="8" fill="#ffffff" stroke="#c9d1dc" strokeWidth="1.5" />
                    <text x="60" y="40" textAnchor="middle" fontFamily="Montserrat, Inter, sans-serif" fontWeight="800" fontSize="9" fill="#1e88e5">ZCE</text>
                    <text x="60" y="54" textAnchor="middle" fontFamily="Montserrat, Inter, sans-serif" fontWeight="800" fontSize="12" fill="#0d3b66" letterSpacing=".5">WATTEL EM</text>
                    <rect x="16" y="60" width="88" height="62" rx="4" fill="#0b0f16" />
                    <text x="22" y="78" fontWeight="800" fontSize="14" fill="#22d3ee">230<tspan fontSize="7">V</tspan></text>
                    <text x="72" y="78" fontWeight="800" fontSize="12" fill="#f472b6">7<tspan fontSize="7">mA</tspan></text>
                    <text x="22" y="97" fontWeight="800" fontSize="14" fill="#f87171">063.0<tspan fontSize="7">A</tspan></text>
                    <text x="22" y="116" fontWeight="800" fontSize="13" fill="#60a5fa">00000.0<tspan fontSize="7">kWh</tspan></text>
                    <rect x="18" y="126" width="84" height="8" rx="2" fill="#f3f5f8" />
                    <circle cx="26" cy="152" r="9" fill="#111827" /><circle cx="48" cy="152" r="9" fill="#dc2626" /><circle cx="72" cy="152" r="9" fill="#111827" /><circle cx="94" cy="152" r="9" fill="#111827" />
                    <text x="48" y="155" textAnchor="middle" fontWeight="800" fontSize="6.5" fill="#ffffff">SET</text>
                    <circle cx="26" cy="152" r="3.5" fill="none" stroke="#ffffff" strokeWidth="1.2" /><path d="M72 148v8M68 152h8" stroke="#ffffff" strokeWidth="1.3" /><path d="M90 153a5 5 0 0 1 8 0M92 155.5a2.5 2.5 0 0 1 4 0" stroke="#ffffff" strokeWidth="1.1" fill="none" />
                  </symbol>
                  <symbol id="zs-dev-point" viewBox="0 0 80 80">
                    <rect x="10" y="10" width="60" height="60" rx="8" fill="white" stroke="#ddd" strokeWidth="1.5" />
                    <circle cx="40" cy="40" r="17" fill="#f5f5f5" stroke="#ccc" strokeWidth="1.5" />
                    <circle cx="33" cy="36" r="2.6" fill="#555" /><circle cx="47" cy="36" r="2.6" fill="#555" /><circle cx="40" cy="47" r="2" fill="#555" />
                  </symbol>
                </defs>
                {elements}
              </svg>
              </div>
              </div>
            )}
            {!empty && (
              <div className="zs-zoom" role="group" aria-label={t('arch_syn_zoom_group')}>
                <button type="button" onClick={() => applyZoom(zoom - ZOOM_STEP)} disabled={zoom <= ZOOM_MIN} aria-label={t('arch_syn_zoom_out')}>
                  <svg viewBox="0 0 24 24"><path d="M5 12h14" /></svg>
                </button>
                <button type="button" className="zs-zoom-pct zs-num" onClick={fitToScreen} title={t('arch_syn_fit')}>{t('arch_syn_pct', { n: Math.round(zoom * 100) })}</button>
                <button type="button" onClick={() => applyZoom(zoom + ZOOM_STEP)} disabled={zoom >= ZOOM_MAX} aria-label={t('arch_syn_zoom_in')}>
                  <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
                </button>
                <button type="button" className={fitMode ? 'zs-zoom-on' : undefined} onClick={fitToScreen} aria-label={t('arch_syn_fit')} title={t('arch_syn_fit')}>
                  <svg viewBox="0 0 24 24"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
                </button>
                <button type="button" className={!fitMode && zoom === 1 ? 'zs-zoom-on' : undefined} onClick={() => applyZoom(1)} aria-label={t('arch_syn_full_width')} title={t('arch_syn_full_width_title')}>
                  <svg viewBox="0 0 24 24"><path d="M3 12h18M7 8l-4 4 4 4M17 8l4 4-4 4" /></svg>
                </button>
              </div>
            )}
          </div>

          {/* Détail (feuille) */}
          {detail && (
            <div className="zs-sheet">
              <h3>{detail.title}</h3>
              <div className="grid gap-1.5">
                {detail.rows.length === 0 && <div className="zs-sub-p">{t('arch_syn_zone_empty')}</div>}
                {detail.rows.map(([label, kw], i) => (
                  <div className="zs-row" key={i}>
                    <div className="truncate">{label}</div>
                    <div className="zs-bar"><i style={{ width: `${Math.round(((kw ?? 0) / detailMax) * 100)}%` }} /></div>
                    <div className="zs-kw zs-num">{fmtKw(kw)}{kw === null ? '' : ' kW'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tuiles */}
          <div className="zs-tiles">
            <div className="zs-tile">
              <div className="zs-k">{t('arch_syn_live_power')}</div>
              <div className="zs-v zs-num">{fmtKw(stats.totalKw)}<small>kW</small></div>
              <div className="zs-d zs-num">
                {stats.totalKwh !== null ? <span>{t('arch_syn_cumulative_index', { kwh: fmtKwh(stats.totalKwh) })}</span> : <span>{stats.wemCount ? t('arch_syn_index_pending') : t('arch_syn_sum_linked')}</span>}
              </div>
              <div className="zs-d zs-num">{t('arch_syn_wem_online', { n: stats.wemCount, m: stats.wemCount - stats.wemOff })}</div>
            </div>
            <div className="zs-tile">
              <div className="zs-k">{t('arch_syn_metering_devices')}</div>
              <div className="zs-v zs-num">{stats.online}<small>{t('arch_syn_online_of', { n: stats.online + stats.off })}</small></div>
              <div className="zs-d">
                <span className="zs-chip zs-chip-ok">{fmtKw(stats.totalEqKw)} kW</span>
                {stats.off > 0 && <span className="zs-chip zs-chip-off">{t('arch_syn_n_offline', { n: stats.off })}</span>}
                {stats.off > 0 && stats.off <= 2 && <span>{stats.offNames.join(', ')}</span>}
              </div>
              <div className="zs-d">{stats.unlinked ? tp('arch_syn_unlinked_points', stats.unlinked) : t('arch_syn_all_linked')}</div>
            </div>
            <div className="zs-tile">
              <div className="zs-k">{t('arch_syn_status_title')}</div>
              <div className="zs-v">{stats.issues ? <>{stats.issues}<small>{tp('arch_syn_to_check', stats.issues)}</small></> : t('arch_syn_normal')}</div>
              <div className="zs-d">
                {stats.issues > 0 && <span className="zs-chip zs-chip-warn">{t('arch_syn_n_offline', { n: stats.issues })}</span>}
                {stats.issues === 0 && <span className="zs-chip zs-chip-ok">{t('arch_syn_all_online')}</span>}
              </div>
              <div className="zs-d zs-num">{t('arch_syn_voltage_refresh', { v: fmtV(stats.voltage) })}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// ─── Styles (portée limitée à .zs-root) ─────────────────────────────────────

const SYNOPTIC_CSS = `
/* Thème clair (celui du dashboard). Le synoptique gère lui-même ses couleurs : on demande au
   navigateur de ne pas lui appliquer son « mode nuit » forcé (inversion automatique). */
.zs-root { color-scheme: only light; background: var(--zs-bg); color: var(--zs-fg);
  --zs-bg:#f5f5f5; --zs-fg:#0f2440; --zs-muted:#6b7a90; --zs-line:#d7dde7; --zs-tile:#fff; --zs-screen:#f9fafc;
  --zs-navy:#0d3b66; --zs-blue:#1e88e5; --zs-ok:#16a34a; --zs-warn:#d97706; --zs-err:#dc2626; --zs-neutral:#7c8aa3;
  --zs-w1:#0d3b66; --zs-w2:#1e88e5; --zs-w3:#0f9d8a; --zs-w4:#f59e0b; --zs-w5:#7c3aed; --zs-w6:#db2777; --zs-w7:#b45309; --zs-w8:#15803d;
  --zs-shadow: rgba(13,59,102,.10); --zs-hit: rgba(30,136,229,.08); }
/* Thème sombre de ZOS (html.dark, bouton Clair / Sombre / Système) : le synoptique passe sur sa
   propre palette (fond bleu nuit, textes clairs, couleurs WattelEM éclaircies) au lieu d'être inversé. */
html.dark .zs-root { color-scheme: dark;
  --zs-bg:#0b1220; --zs-fg:#e8eef7; --zs-muted:#94a3b8; --zs-line:#22324c; --zs-tile:#132342; --zs-screen:#0e1a2e;
  --zs-navy:#2f5f92; --zs-blue:#4aa3f0; --zs-ok:#22c55e; --zs-warn:#f59e0b; --zs-err:#f87171; --zs-neutral:#8b9bb5;
  --zs-w1:#7db3e6; --zs-w2:#4aa3f0; --zs-w3:#2fc4ad; --zs-w4:#ffb547; --zs-w5:#a78bfa; --zs-w6:#f472b6; --zs-w7:#f59e0b; --zs-w8:#4ade80;
  --zs-shadow: rgba(0,0,0,.45); --zs-hit: rgba(74,163,240,.14); }
html.dark .zs-root .zs-sil { filter: brightness(.92); }
html.dark .zs-root .zs-pill { background: var(--zs-blue); color:#06111f; }
html.dark .zs-root .zs-zoom button { color: var(--zs-fg); }
.zs-num { font-variant-numeric: tabular-nums; }
.zs-screen { background: var(--zs-screen); border: 1px solid var(--zs-line); border-radius: 18px; overflow: hidden; box-shadow: 0 10px 40px var(--zs-shadow); }
.zs-status { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:8px 16px; font-size:12.5px; color:var(--zs-muted); border-bottom:1px solid var(--zs-line); }
.zs-status-l, .zs-status-r { display:flex; align-items:center; gap:10px; min-width:0; }
.zs-sig { display:inline-flex; gap:2px; align-items:flex-end; height:12px; }
.zs-sig i { width:3px; background:var(--zs-fg); border-radius:1px; display:block; }
.zs-on { display:inline-flex; align-items:center; gap:6px; color:var(--zs-fg); font-weight:600; }
.zs-on b { width:8px; height:8px; border-radius:50%; background:var(--zs-ok); display:inline-block; animation: zs-pulse 1.8s ease-in-out infinite; }
.zs-on b.zs-off-dot { background:var(--zs-muted); animation:none; }
.zs-name { color:var(--zs-fg); font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.zs-pill { background:var(--zs-navy); color:#fff; font-weight:600; font-size:11.5px; padding:4px 10px; border-radius:7px; }
.zs-canvas { padding: 8px 12px 0; position:relative; }
.zs-viewport { overflow:auto; touch-action: pan-x pan-y; -webkit-overflow-scrolling: touch; scrollbar-width: thin; padding: ${VIEWPORT_PAD}px; border-radius: 12px; background: var(--zs-screen); }
.zs-vp-embedded { height: clamp(320px, calc(100vh - 400px), 820px); }
.zs-vp-full { height: clamp(360px, 68vh, 900px); }
/* La scène centre le dessin quand il est plus petit que la fenêtre ; s'il déborde, les marges auto
   retombent à 0 et la fenêtre défile normalement (rien n'est rogné en haut ni à gauche). */
.zs-stage { display:flex; min-width:100%; min-height:100%; width:max-content; }
.zs-syn { width:100%; height:auto; display:block; margin:auto; flex:none; transition: width .12s ease-out; }
.zs-zoom .zs-zoom-on { background: var(--zs-hit); color: var(--zs-blue); }
.zs-root.zs-embedded { min-height:100%; }
.zs-zoom { position:sticky; bottom:10px; margin: -50px 6px 6px auto; width:max-content; display:inline-flex; align-items:center; gap:2px; padding:3px; border-radius:12px; background:var(--zs-tile); border:1px solid var(--zs-line); box-shadow: 0 6px 20px var(--zs-shadow); z-index:2; }
.zs-zoom button { width:44px; height:40px; border:0; border-radius:9px; background:transparent; color:var(--zs-navy); cursor:pointer; display:inline-flex; align-items:center; justify-content:center; font: 600 12px 'Inter',system-ui,sans-serif; touch-action: manipulation; }
.zs-zoom button:hover { background: var(--zs-hit); }
.zs-zoom button:disabled { opacity:.35; cursor:default; background:transparent; }
.zs-zoom button svg { width:18px; height:18px; fill:none; stroke:currentColor; stroke-width:2.2; stroke-linecap:round; stroke-linejoin:round; }
.zs-zoom .zs-zoom-pct { width:56px; }
@media print { .zs-zoom { display:none; } .zs-viewport { height:auto !important; overflow:visible; } .zs-stage { width:100%; } .zs-syn { width:100% !important; } }
.zs-syn text { font-family: 'Inter', system-ui, sans-serif; fill: var(--zs-fg); }
.zs-lbl { font-size:13px; font-weight:600; }
.zs-sub { font-size:11px; fill:var(--zs-muted) !important; }
.zs-val { font: 800 30px 'Montserrat','Inter',system-ui,sans-serif; font-variant-numeric: tabular-nums; }
.zs-u { font: 500 14px 'Inter',system-ui,sans-serif; fill:var(--zs-muted); }
.zs-hubval { font: 800 24px 'Montserrat','Inter',system-ui,sans-serif; font-variant-numeric: tabular-nums; }
.zs-u2 { font: 500 12px 'Inter',system-ui,sans-serif; fill:var(--zs-muted); }
.zs-port { font: 800 9.5px 'Inter',system-ui,sans-serif; fill:#fff !important; letter-spacing:.04em; }
.zs-pico { fill:none; stroke:#fff; stroke-width:1.9; stroke-linecap:round; stroke-linejoin:round; }
.zs-legend { font-size:10.5px; fill:var(--zs-muted) !important; }
.zs-portkw { font-size:10.5px; font-weight:600; font-variant-numeric: tabular-nums; }
.zs-zone { font-size:13px; font-weight:700; }
.zs-zkind { font-weight:500; fill:var(--zs-muted); }
.zs-zk path { fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.zs-chip-svg text { font: 800 8px 'Inter',system-ui,sans-serif; letter-spacing:.06em; fill:#fff !important; }
.zs-chip-svg circle { fill:#fff; }
.zs-st-ok rect { fill:var(--zs-ok); }
.zs-st-off rect { fill:var(--zs-err); }
.zs-st-na rect { fill:var(--zs-muted); }
.zs-off .zs-chip-svg { opacity:1; }
.zs-portoff { fill:var(--zs-err) !important; font-size:8.5px; font-weight:800; letter-spacing:.05em; }
.zs-dot-err { fill:var(--zs-err); stroke:#fff; stroke-width:1.5; }
.zs-live { animation: zs-pulse 1.8s ease-in-out infinite; }
.zs-ztot { font-size:11px; fill:var(--zs-muted) !important; font-variant-numeric: tabular-nums; }
.zs-eq { font-size:11.5px; font-weight:600; }
.zs-eqkw { font: 700 15px 'Montserrat','Inter',system-ui,sans-serif; font-variant-numeric: tabular-nums; }
.zs-eqkw .zs-u { font-size:10px; }
.zs-eqmeta { font-size:9.5px; fill:var(--zs-muted) !important; }
.zs-eqmeta-off { fill:var(--zs-err) !important; font-weight:600; }
.zs-more { font-size:12px; font-weight:600; fill:var(--zs-blue) !important; }
.zs-badge { font: 800 9px 'Inter',system-ui,sans-serif; fill:#fff !important; }
.zs-ico { fill:none; stroke:var(--zs-fg); stroke-width:1.7; stroke-linecap:round; stroke-linejoin:round; }
.zs-ico.zs-accent { stroke:var(--zs-blue); }
.zs-ico.zs-hub { stroke:var(--zs-navy); stroke-width:1.9; }
.zs-wire { fill:none; stroke:var(--zs-line); stroke-width:2; }
.zs-flow { fill:none; stroke-width:2.2; stroke-linecap:round; stroke-dasharray:5 9; animation: zs-dash 2.6s linear infinite; }
.zs-flow.zs-idle { stroke:var(--zs-line) !important; animation:none; stroke-dasharray:2 7; }
.zs-flow.zs-fast { animation-duration:1.2s; }
.zs-hit { fill:transparent; cursor:pointer; outline:none; transition: fill .15s; }
.zs-hit:hover, .zs-hit:focus-visible { fill: var(--zs-hit); }
.zs-off .zs-sil { opacity:.38; }
.zs-off .zs-eqkw, .zs-off .zs-portkw { fill:var(--zs-muted); }
.zs-lane { stroke-width:2; opacity:.55; }
.zs-dot-ok { fill:var(--zs-ok); }
.zs-dot-off { fill:var(--zs-muted); }
@keyframes zs-dash { to { stroke-dashoffset:-28; } }
@keyframes zs-pulse { 0%,100% { opacity:1 } 50% { opacity:.35 } }
@media (prefers-reduced-motion: reduce) { .zs-flow, .zs-on b, .zs-live { animation:none; } }
.zs-empty { padding: 48px 16px; text-align:center; color:var(--zs-muted); display:grid; justify-items:center; gap:8px; }
.zs-empty p { margin:0; color:var(--zs-fg); font-weight:600; }
.zs-sub-p { color:var(--zs-muted) !important; font-weight:400 !important; font-size:12.5px; }
.zs-tiles { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:12px; padding: 6px 16px 14px; }
.zs-tile { background:var(--zs-tile); border:1px solid var(--zs-line); border-radius:12px; padding:12px 14px 10px; display:grid; gap:4px; min-width:0; position:relative; overflow:hidden; align-content:start; }
.zs-tile::after { content:""; position:absolute; left:14px; right:14px; bottom:0; height:3px; border-radius:3px 3px 0 0; background:var(--zs-blue); opacity:.7; }
.zs-k { font-size:12px; font-weight:600; color:var(--zs-muted); }
.zs-v { font: 800 26px 'Montserrat','Inter',system-ui,sans-serif; line-height:1.1; color:var(--zs-fg); }
.zs-v small { font: 500 12px 'Inter',system-ui,sans-serif; color:var(--zs-muted); margin-left:4px; }
.zs-d { font-size:11.5px; color:var(--zs-muted); display:flex; gap:8px; align-items:center; flex-wrap:wrap; min-height:18px; }
.zs-chip { display:inline-flex; align-items:center; gap:5px; border-radius:4px; padding:1px 7px; font-size:10.5px; font-weight:700; color:#fff; }
.zs-chip-ok { background:var(--zs-ok); }
.zs-chip-off { background:var(--zs-muted); }
.zs-chip-warn { background:var(--zs-warn); }
.zs-sheet { margin: 0 16px 12px; border:1px solid var(--zs-line); border-radius:12px; background:var(--zs-tile); padding:12px 14px; display:grid; gap:8px; }
.zs-sheet h3 { margin:0; font: 700 12px 'Inter',system-ui,sans-serif; letter-spacing:.05em; text-transform:uppercase; color:var(--zs-muted); }
.zs-row { display:grid; grid-template-columns: minmax(120px,1fr) minmax(0,3fr) 76px; align-items:center; gap:10px; font-size:13px; }
.zs-bar { height:7px; border-radius:999px; background:var(--zs-line); overflow:hidden; }
.zs-bar i { display:block; height:100%; background:var(--zs-blue); }
.zs-kw { text-align:right; font-weight:600; }
@media (max-width: 720px) { .zs-tiles { grid-template-columns:1fr; } .zs-row { grid-template-columns: 1fr 70px; } .zs-bar { grid-column: 1 / -1; } .zs-name { max-width:40vw; } }
@media print { .zs-header { display:none; } .zs-root { background:#fff; } .zs-screen { box-shadow:none; border-color:#ccc; } .zs-flow { animation:none; } }
`;
